import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Displays the 8 Pillars one at a time with a slow fade-in / fade-out.
 * Total loop ~ 40s (each pillar shown ~5s including transition).
 */

const pillars = [
  { n: "01", w: "Self Discovery",         c: "var(--brand)"  },
  { n: "02", w: "Emotional Intelligence", c: "var(--violet)" },
  { n: "03", w: "Confidence & Voice",     c: "var(--mint)"   },
  { n: "04", w: "Navigating Influences",  c: "var(--brand)"  },
  { n: "05", w: "Resilience & Grit",      c: "var(--violet)" },
  { n: "06", w: "Decision Making",        c: "var(--mint)"   },
  { n: "07", w: "Self Worth",             c: "var(--brand)"  },
  { n: "08", w: "Purpose & Direction",    c: "var(--violet)" },
];

const HOLD_MS = 5000; // time each pillar sits at full opacity

export default function MarqueeBand() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % pillars.length);
    }, HOLD_MS);
    return () => clearInterval(id);
  }, [paused]);

  const current = pillars[idx];

  return (
    <section
      aria-label="The 8 pillars we build over a season"
      className="paper-bg border-y hairline border-y-[1px]"
      data-testid="marquee-band"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-24">
        <div className="grid grid-cols-12 gap-6 items-center">
          {/* Left — small mono label */}
          <div className="col-span-12 md:col-span-3">
            <div className="font-mono-editorial text-[11px] tracking-[0.32em] text-[color:var(--muted)]">
              — THE 8 PILLARS
            </div>
            <div className="mt-2 text-[color:var(--ink)]/70 text-sm max-w-[26ch]">
              What we build in a season. One pillar at a time.
            </div>
          </div>

          {/* Center — the fading pillar */}
          <div className="col-span-12 md:col-span-6 relative h-[110px] md:h-[160px] flex items-center justify-center overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.n}
                initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -14, filter: "blur(6px)" }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 flex flex-col items-center justify-center text-center"
                data-testid={`pillar-slide-${current.n}`}
              >
                <span
                  className="font-serif-editorial italic text-lg md:text-xl mb-2"
                  style={{ color: current.c }}
                >
                  Pillar {current.n}
                </span>
                <h3 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tight text-[color:var(--ink)] max-w-[16ch]">
                  {current.w}
                </h3>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right — pillar count / dot progress */}
          <div className="col-span-12 md:col-span-3 md:justify-self-end">
            <div className="flex flex-wrap gap-1.5 md:justify-end" role="tablist" aria-label="8 pillars">
              {pillars.map((p, i) => (
                <button
                  key={p.n}
                  onClick={() => setIdx(i)}
                  aria-label={`Show pillar ${p.n} — ${p.w}`}
                  className="w-8 h-8 flex items-center justify-center border hairline text-[10px] font-mono-editorial tracking-wider transition-colors"
                  style={{
                    background: i === idx ? "var(--ink)" : "transparent",
                    color: i === idx ? "var(--inv)" : "var(--ink)",
                    borderColor: "var(--ink)",
                  }}
                  data-testid={`pillar-dot-${p.n}`}
                >
                  {p.n}
                </button>
              ))}
            </div>
            <div className="mt-3 font-mono-editorial text-[10px] tracking-[0.28em] text-[color:var(--muted)] md:text-right">
              {idx + 1} / {pillars.length}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
